import { ForemanPhaseLike } from './foreman-shapes';

/** Viewpoint company number for the Co 15 billable-default rule (WI 31493). */
const COMPANY_15 = 15;

/**
 * Pick which work orders are visible by default on a foreman time-entry panel.
 *
 * Extracted from `individual-time-entry-web-save.service.ts`, where it sat at the bottom
 * of an Angular `@Injectable` marked "Exported for unit tests". It is pure, so it belongs
 * in a util both platforms can call rather than in a web service.
 *
 * Rule: show every work order when the job has few enough. Above `maxVisible`, take the
 * ones whose display name matches a priority substring first, then fill the remainder in
 * list order, so a foreman always sees the phases that matter most on a crowded job.
 */
export function selectDefaultVisibleWorkOrderIds<T extends ForemanPhaseLike>(
  workOrders: T[],
  maxVisible: number,
  prioritySubstrings: string[]
): string[] {
  // Type predicate rather than a cast: `foremanPhaseID` is nullable on the real wire
  // contract (see ForemanPhaseLike / #30151), and a plain truthiness filter does NOT
  // narrow the element type through the later `.map`. This keeps the return `string[]`
  // honestly, with no non-null assertion papering over an empty or missing id.
  const withIds = workOrders.filter(
    (wo): wo is T & { foremanPhaseID: string } => !!wo.foremanPhaseID
  );
  if (withIds.length <= maxVisible) {
    return withIds.map((wo) => wo.foremanPhaseID);
  }

  const displayName = (wo: T) => wo.phaseName || wo.description || '';

  const isPriority = (wo: T) =>
    prioritySubstrings.some((sub) => displayName(wo).includes(sub));

  const selected: (T & { foremanPhaseID: string })[] = [];
  for (const wo of withIds) {
    if (selected.length >= maxVisible) break;
    if (isPriority(wo)) selected.push(wo);
  }
  for (const wo of withIds) {
    if (selected.length >= maxVisible) break;
    if (!isPriority(wo)) selected.push(wo);
  }

  return selected.map((wo) => wo.foremanPhaseID);
}

/**
 * Company-aware default work order selection (WI 31493).
 *
 * Company 15 billable jobs must NOT blanket-default the billable phase (the old rule matched
 * the generic 1111 template phase by name, which looked right only because most Co 15 jobs
 * carry exactly one billable phase). Rule: for Company 15, default to the single billable work
 * order when the job has exactly one; when it has two or more billable work orders, default to
 * NONE so the foreman must pick. The non-billable default is unchanged, and every other company
 * keeps the existing {@link selectDefaultVisibleWorkOrderIds} behavior untouched.
 *
 * "Billable" is `wo.isBillable === true`; a null/undefined flag counts as non-billable so an
 * ambiguous phase is never auto-preselected as the billable default.
 */
export function selectDefaultWorkOrderIdsForCompany<T extends ForemanPhaseLike>(
  workOrders: T[],
  vpCompanyID: number | null | undefined,
  maxVisible: number,
  prioritySubstrings: string[]
): string[] {
  if (vpCompanyID !== COMPANY_15) {
    return selectDefaultVisibleWorkOrderIds(
      workOrders,
      maxVisible,
      prioritySubstrings
    );
  }

  const billable = workOrders.filter((wo) => wo.isBillable === true);
  const nonBillable = workOrders.filter((wo) => wo.isBillable !== true);

  // Exactly one billable WO -> preselect it. Two or more -> none (blank; foreman picks).
  const billableDefault =
    billable.length === 1 && billable[0].foremanPhaseID
      ? [billable[0].foremanPhaseID]
      : [];

  // Non-billable default is unchanged. Reserve the slot(s) the billable default takes so the
  // combined set still respects maxVisible.
  const nonBillableDefault = selectDefaultVisibleWorkOrderIds(
    nonBillable,
    Math.max(0, maxVisible - billableDefault.length),
    prioritySubstrings
  );

  return [...new Set([...billableDefault, ...nonBillableDefault])];
}

/**
 * Whether a phase/work order is blocked for new timesheet entry in Viewpoint (#30262).
 *
 * VP sends `udTimesheetBlock` as a `char(1)` where `'Y'` means blocked. We normalize casing
 * and surrounding whitespace so a stray `'y'` or padded value can't slip a blocked phase
 * back into the entry grid. Every other value (including null, `''`, and `'N'`) is unblocked.
 */
export function isWorkOrderTimesheetBlocked(
  // Pick, not ForemanPhaseLike: the office WorkOrder model carries the same flag but
  // no foremanPhaseID, and this check only ever reads the one field.
  wo: Pick<ForemanPhaseLike, 'udTimesheetBlock'>
): boolean {
  return (wo.udTimesheetBlock ?? '').trim().toUpperCase() === 'Y';
}

export interface BlockedWorkOrderVisibility {
  /** Work orders to show in the entry grid, in list order. */
  visibleWorkOrderIds: string[];
  /**
   * Blocked work orders the employee already has time on this week. They stay visible (so the
   * time is not stranded) but the caller should warn the employee to move it off the blocked
   * phase. A subset of `visibleWorkOrderIds`.
   */
  warnMoveWorkOrderIds: string[];
}

/**
 * Decide which work orders a foreman/office time-entry grid shows once Viewpoint's timesheet
 * block is honored (#30262).
 *
 * Rule: a blocked phase is hidden so no new time can land on it, UNLESS the employee already
 * has their own time on it this week. In that case it stays visible AND is flagged, so the UI
 * can prompt the employee to move the hours off the blocked phase rather than silently
 * stranding time they can no longer see. Unblocked phases are always visible.
 *
 * Pure and framework-agnostic so web (individual job panel, time manager, allocation sheet)
 * and mobile (select-work-order) and the office timekeeping dropdown all apply the identical
 * rule. `phaseIdsWithOwnTimeThisWeek` is the set of `foremanPhaseID`s the employee has booked
 * time against in the active week; the caller owns computing it for its own labor domain.
 *
 * Work orders with no usable `foremanPhaseID` are dropped from both sets, matching
 * {@link selectDefaultVisibleWorkOrderIds}: an id-less phase cannot be keyed against booked
 * time nor selected as a grid row.
 */
export function resolveBlockedWorkOrderVisibility<T extends ForemanPhaseLike>(
  workOrders: T[],
  phaseIdsWithOwnTimeThisWeek: Iterable<string | null | undefined>
): BlockedWorkOrderVisibility {
  const ownTime = new Set<string>();
  for (const id of phaseIdsWithOwnTimeThisWeek) {
    if (id) ownTime.add(id);
  }

  const visibleWorkOrderIds: string[] = [];
  const warnMoveWorkOrderIds: string[] = [];

  for (const wo of workOrders) {
    const id = wo.foremanPhaseID;
    if (!id) continue;

    if (!isWorkOrderTimesheetBlocked(wo)) {
      visibleWorkOrderIds.push(id);
      continue;
    }

    // Blocked: only surfaces if the employee already booked time on it this week.
    if (ownTime.has(id)) {
      visibleWorkOrderIds.push(id);
      warnMoveWorkOrderIds.push(id);
    }
  }

  return { visibleWorkOrderIds, warnMoveWorkOrderIds };
}
